import React, { useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import { Box, PanelRow, Tooltip } from '@getro/rombo';
import { Flex, Image, Text, Link } from 'rebass/styled-components';
import moment from 'moment';
import BlankMessage from 'components/molecules/BlankMessage';
import SearchIcon from '../../../assets/icon-effortless-search.svg';
import ForbiddenIcon from '../../../assets/icon-forbidden.svg';
import JobSource from '../../../components/molecules/jobSource';
import NetworkSchema from '../../../schemas/network';
import UserSchema from '../../../schemas/user';
import OrganizationSchema from '../../../schemas/organization';

const CareersPages = ({ company, activeNetwork, currentUser }) => {
  const { slug } = activeNetwork;
  const { domain } = company;
  const { firstName, lastName, email } = currentUser;
  const {
    id: companyId,
    name: companyName,
    websiteUrl: companyUrl,
    organizationCareersPages: ocps,
    jobSourceCustomerFacingStatus,
    slug: companySlug,
    activeJobsCount,
    lastSuccessfullyScrapedAt,
  } = company;

  const jobSourceChangeUrl = useMemo(() => {
    const qp = new URLSearchParams({
      firstname: firstName || '',
      companyname: companyName || '',
      email: email || '',
      lastname: lastName || '',
      companyid: String(companyId ?? ''),
      companyurl: companyUrl || '',
    }).toString();
    return `https://getro-forms.typeform.com/to/yMmzpUKy#${qp}`;
  }, [companyId, companyName, companyUrl, email, firstName, lastName]);

  const hasNoDomain = !domain;

  const renderContent = () => {
    if (hasNoDomain) {
      return (
        <Flex flexDirection={['column']} alignItems="center" textAlign="center" pb="12px">
          <Image src={ForbiddenIcon} mb={[3]} width="64px" height="64px" />
          <Text mb="8px" fontWeight="semibold">
            Extracted jobs are not available
          </Text>
          <Text>
            Because this company has no website, we are not able to add a job source. You can always post jobs below.
          </Text>
        </Flex>
      );
    }

    if (jobSourceCustomerFacingStatus === 'Off') {
      return (
        <Flex flexDirection={['column']} alignItems="center" textAlign="center">
          <Image src={ForbiddenIcon} width="64px" pb="4px" />
          <BlankMessage title="Automated job extraction is switched off for this company" pt="4px">
            <Text>To turn it back on, email</Text>
            <a href="mailto:help@getro.com." target="_blank" rel="noopener noreferrer">
              help@getro.com
            </a>
            .
          </BlankMessage>
        </Flex>
      );
    }

    if (jobSourceCustomerFacingStatus === 'In Setup') {
      return (
        <Flex flexDirection={['column']} alignItems="center" textAlign="center">
          <Image src={SearchIcon} width="64px" pb="4px" />
          <BlankMessage title="We are connecting the company jobs to your network" pt="4px">
            <Text>
              It usually takes 2 business days, we will email you when it is done.{' '}
              <Link
                color="primary"
                href="https://help.getro.com/support/solutions/articles/65000179707-company-job-sources"
                target="_blank"
                rel="noopener noreferrer"
              >
                Learn more
              </Link>
              .
            </Text>
            <Text>
              You can also suggest a source{' '}
              <Link color="primary" to={jobSourceChangeUrl} target="_blank" rel="noopener noreferrer">
                here
              </Link>
              .
            </Text>
          </BlankMessage>
        </Flex>
      );
    }

    if (jobSourceCustomerFacingStatus === 'In Review') {
      return (
        <Flex flexDirection={['column']} alignItems="center" textAlign="center">
          {lastSuccessfullyScrapedAt && (
            <PanelRow title="Last updated" mb="12px" textAlign="left">
              <Tooltip
                content={moment(lastSuccessfullyScrapedAt).format('D MMM, YYYY [at] hh:mm a')}
                trigger={<span>{moment().from(lastSuccessfullyScrapedAt, true)} ago</span>}
              />
            </PanelRow>
          )}
          {activeJobsCount > 0 && (
            <PanelRow title="Jobs" textAlign="left">
              <Text>{activeJobsCount} Total</Text>
            </PanelRow>
          )}
          <Image src={SearchIcon} width="64px" pt="8px" pb="4px" />
          <BlankMessage title="Our job source experts are reviewing the job source(s) for this company." pt="4px">
            <Text>
              Job extraction should be back in action within 2 business days. You can suggest a source,{' '}
              <Link color="primary" href={jobSourceChangeUrl} target="_blank" rel="noopener noreferrer">
                here
              </Link>
              .
            </Text>
          </BlankMessage>
        </Flex>
      );
    }

    if (jobSourceCustomerFacingStatus === 'Unavailable') {
      return (
        <Flex flexDirection={['column']} alignItems="center" textAlign="center">
          <Image src={SearchIcon} width="64px" pb="4px" />
          <BlankMessage title="We couldn't find a job source." pt="4px">
            <Text>
              We&apos;ll check again every month, or you can suggest a source{' '}
              <Link color="primary" href={jobSourceChangeUrl} target="_blank" rel="noopener noreferrer">
                here
              </Link>
              .{' '}
              <Link
                color="primary"
                href="https://help.getro.com/support/solutions/articles/65000179707-company-job-sources"
                target="_blank"
                rel="noopener noreferrer"
              >
                Learn more
              </Link>
            </Text>
            <Text pt="8px">
              You can also post jobs manually{' '}
              <Link
                color="primary"
                as={NavLink}
                to={`/networks/${slug}/companies/${companySlug}/post-job`}
                rel="noopener noreferrer"
              >
                here
              </Link>
              .
            </Text>
          </BlankMessage>
        </Flex>
      );
    }

    if (jobSourceCustomerFacingStatus === 'Live') {
      return (
        <>
          {ocps.map((ocp) => (
            <React.Fragment key={ocp.id}>
              <JobSource source={ocp} currentUser={currentUser} company={company} />
            </React.Fragment>
          ))}
          <Text mt="8px">
            To report a change in job source(s),{' '}
            <Link color="primary" href={jobSourceChangeUrl} target="_blank" rel="noopener noreferrer">
              click here
            </Link>
          </Text>
        </>
      );
    }

    return null;
  };

  return <Box title="Job extraction">{renderContent()}</Box>;
};

CareersPages.propTypes = {
  company: OrganizationSchema.isRequired,
  activeNetwork: NetworkSchema.isRequired,
  currentUser: UserSchema.isRequired,
};

export default CareersPages;
